import {ref} from 'vue';
import {defineStore} from 'pinia';
import {User} from '~/types/interfaces/entitites/UserInterface';
import {LoginRequest, RegisterRequest} from '~/types/interfaces/auth/AuthInterface';
import {ErrorInterface} from "~/types/interfaces/ErrorInterface";
import {useCSRF} from "~/stores/csrf";
import {useAPI} from "~/composables/useAPI";
import {useToken} from "~/stores/token";

export const useAuthStore = defineStore('auth', () => {
    const returnUrl = ref<string | null>(null);
    const error = ref<ErrorInterface | null>(null);
    const user = ref<User>({} as User);
    const isAuthenticated = ref(false);
    const csrfStore = useCSRF();
    const useApi = useAPI();
    const tokenStore = useToken();

    // computed()s become getters
    const currentUser = computed(() => user.value)
    const currentError = computed(() => error.value)
    const currentRole = computed(() => user.value.role)
    const currentReturnUrl = computed(() => returnUrl.value)

    function setAuth(authUser: User) {
        isAuthenticated.value = true;
        user.value = authUser;
        error.value = null;
    }

    function setReturnUrl(url: string) {
        returnUrl.value = url;
    }

    function setError(errorData: any) {
        error.value = errorData;
    }

    function purgeAuth() {
        isAuthenticated.value = false;
        user.value = {} as User;
        error.value = null;
    }

    async function login(credentials: LoginRequest) {
        resetErrors();

        // Si queremos funcionar con la session de Laravel.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        }).then(({data}) => {
            // Si queremos funcionar con la session de Laravel.
            // El login nos devolvería el usuario.
            if (csrfStore.token && data) {
                setAuth(data);
            }
            tokenStore.clearToken();
            tokenStore.setToken(data.token);
            setAuth({} as User);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function getUser(refresh = false) {
        if (!refresh) {
            if (isAuthenticated.value && user.value) {
                return user.value
            }
        }

        const response = await useApi.apiFetch('/administration/profile/me')
            .catch(({response}) => {
                purgeAuth();
                if (response) {
                    setError(response._data);
                }
            });

        if (response) {
            setAuth(response.data);
        }

        return user.value
    }

    async function logout() {
        resetErrors();
        if (isAuthenticated.value && user.value) {
            await useApi.apiFetch('/auth/logout', {
                method: 'POST',
            }).then(({data}) => {
                if (csrfStore.token) {
                    csrfStore.clearToken()
                }
                if (tokenStore.currentToken) {
                    tokenStore.clearToken()
                }
                purgeAuth();
            }).catch(({response}) => {
                if (response) {
                    setError(response._data);
                } else {
                    setError({
                            application_code: '',
                            data: {},
                            message: 'No hay conexión',
                            status: 'Error',
                        }
                    );
                }
            });
        } else {
            if (csrfStore.token) {
                csrfStore.clearToken()
            }
            if (tokenStore.currentToken) {
                tokenStore.clearToken()
            }
            purgeAuth();
        }
    }

    function resetErrors() {
        error.value = null;
    }

    return {
        error,
        user,
        isAuthenticated,
        currentUser,
        currentError,
        currentRole,
        currentReturnUrl,
        login,
        logout,
        getUser,
        setReturnUrl,
        resetErrors
    };
});
